/**
 * Privacy — first-pass policy content covering:
 *   1. How we measure traffic (first-party cookieless beacon)
 *   2. Third-party tracking (Meta Pixel — disclosure only)
 *   3. PostHog historical disclosure (template snippet removal)
 *
 * Feb 2026 Lane A. Voice is friendly + dog-loving, not corporate
 * legalese. Factually rigorous — every claim maps to real code
 * in this repo (see cross-refs in each section).
 *
 * Family-L guard: Tailwind tokens + CSS custom properties for
 * colors. No inline hex.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import PublicPageShell from "@/components/PublicPageShell";

// Last-updated date — bumped whenever the policy content changes.
// Format: YYYY-MM-DD (unambiguous for international readers).
const LAST_UPDATED = "2026-07-26";

function TocLink({ to, children }) {
  return (
    <a
      href={to}
      className="text-sm underline"
      style={{ color: "var(--pp-purple)" }}
    >
      {children}
    </a>
  );
}

function SectionHeading({ id, children }) {
  return (
    <h2
      id={id}
      className="pp-h2 mt-10 scroll-mt-20"
      data-testid={`privacy-section-${id}`}
    >
      {children}
    </h2>
  );
}

export default function Privacy() {
  return (
    <PublicPageShell mainClass="px-6 py-14">
      <article
        className="pp-wrap mx-auto max-w-3xl"
        data-testid="privacy-page"
      >
        <span className="pp-eyebrow" data-testid="privacy-eyebrow">
          <span className="dot" />
          Legal
        </span>
        <h1
          className="pp-h1 mt-6"
          data-testid="privacy-headline"
        >
          Privacy Policy
        </h1>
        <p
          className="mt-2 text-sm"
          style={{ color: "var(--pp-text-soft)" }}
          data-testid="privacy-last-updated"
        >
          Last updated {LAST_UPDATED}
        </p>

        <p
          className="pp-customer-p mt-6 text-left"
          data-testid="privacy-intro"
        >
          We take your data seriously and we&apos;re writing this
          honestly. Below is what we actually do with the data our
          website sees — no marketing spin, no legalese, and no
          claims we can&apos;t back up in code.
        </p>

        {/* Table of contents */}
        <nav
          className="mt-8 rounded-lg border p-4"
          style={{
            background: "var(--pp-surface-2)",
            borderColor: "var(--pp-border)",
          }}
          aria-label="Table of contents"
          data-testid="privacy-toc"
        >
          <div
            className="text-[11px] font-bold uppercase tracking-[0.06em]"
            style={{ color: "var(--pp-text-soft)" }}
          >
            On this page
          </div>
          <ul className="mt-2 space-y-1">
            <li>
              <TocLink to="#traffic-measurement">
                How we measure traffic
              </TocLink>
            </li>
            <li>
              <TocLink to="#meta-pixel">
                Third-party tracking — Meta Pixel
              </TocLink>
            </li>
            <li>
              <TocLink to="#posthog-disclosure">
                PostHog historical disclosure
              </TocLink>
            </li>
          </ul>
        </nav>

        {/* -------------------------------------------------------- */}
        {/* SECTION 1 — How we measure traffic                       */}
        {/* -------------------------------------------------------- */}
        <SectionHeading id="traffic-measurement">
          How we measure traffic
        </SectionHeading>
        <p
          className="pp-customer-p mt-4 text-left"
          data-testid="privacy-traffic-body"
        >
          To understand which pages people actually use, we run a
          small first-party visit counter that lives on our own
          servers. Here&apos;s the honest breakdown of how it works:
        </p>
        <ul
          className="pp-customer-p mt-3 list-disc space-y-2 pl-6 text-left"
          data-testid="privacy-traffic-facts"
        >
          <li>
            <strong>No cookies for tracking.</strong> We don&apos;t
            drop any cookies just to count visits. We use
            localStorage only for things you&apos;d expect us to
            remember (your preferences, your consent choice if you
            make one) — never as a tracking beacon.
          </li>
          <li>
            <strong>Anonymous visitor hash that rotates daily.</strong>{" "}
            Each visit gets a one-way hash so we can tell two clicks
            in the same session apart from two separate people. That
            hash <em>rotates every 24 hours UTC</em>, so there&apos;s
            no way to link what you did today to what you did
            yesterday. By design.
          </li>
          <li>
            <strong>Raw IP addresses are never stored.</strong> Your
            IP touches our server for a millisecond as part of the
            hash calculation, then it&apos;s dropped. It never lands
            in any database row.
          </li>
          <li>
            <strong>Raw event data lives for 180 days, then
            expires.</strong> Aggregated daily rollups (page X had Y
            visits on date Z) stay indefinitely because they&apos;re
            not tied to individuals.
          </li>
          <li>
            <strong>Data stays on our servers.</strong> Nothing from
            this first-party counter is sold, shared, or sent to any
            third party. Meta Pixel is a separate thing — see below.
          </li>
        </ul>
        <p
          className="pp-customer-p mt-4 text-left"
          data-testid="privacy-traffic-what-we-measure"
        >
          <strong>What we actually see:</strong> page views, an
          approximate count of unique visitors, top pages, which
          site (if any) referred you here, UTM campaign tags on
          shared links, and a coarse device class (mobile / tablet /
          desktop only — no operating system, no browser version,
          no fingerprint).
        </p>

        {/* -------------------------------------------------------- */}
        {/* SECTION 2 — Meta Pixel                                   */}
        {/* -------------------------------------------------------- */}
        <SectionHeading id="meta-pixel">
          Third-party tracking — Meta Pixel
        </SectionHeading>
        <p
          className="pp-customer-p mt-4 text-left"
          data-testid="privacy-meta-body"
        >
          We run Meta&apos;s advertising measurement tag on
          non-admin pages of this site. In plain English:
        </p>
        <ul
          className="pp-customer-p mt-3 list-disc space-y-2 pl-6 text-left"
          data-testid="privacy-meta-facts"
        >
          <li>
            <strong>What it is.</strong> A small script from Meta
            (Facebook / Instagram&apos;s parent company) that tells
            them when someone lands on our pages. It&apos;s how we
            measure whether our Instagram + Facebook ads actually
            bring people who care about clean yards.
          </li>
          <li>
            <strong>What flows to Meta.</strong> The URL you visited,
            what site referred you here, standard browser signals
            Meta collects across every site that runs their pixel,
            and event-level pings like &quot;PageView&quot; when you
            change pages inside our site. We don&apos;t send Meta
            your name, your email, your address, your dog&apos;s
            name, or anything you type into a form on our site.
          </li>
          <li>
            <strong>Meta&apos;s data handling is Meta&apos;s
            responsibility, not ours.</strong> Once data reaches
            Meta, it&apos;s governed by their privacy policy — not
            this one. Their policy lives at{" "}
            <a
              href="https://www.facebook.com/privacy/policy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
              style={{ color: "var(--pp-purple)" }}
              data-testid="privacy-meta-policy-link"
            >
              facebook.com/privacy/policy
            </a>
            .
          </li>
          <li>
            <strong>The pixel ID is public.</strong> Every site that
            runs Meta Pixel exposes its pixel ID in outbound
            requests — it&apos;s not a secret. Ours is{" "}
            <code
              className="rounded px-1 text-[13px]"
              style={{
                background: "var(--pp-surface-2)",
                color: "var(--pp-text)",
              }}
              data-testid="privacy-meta-pixel-id"
            >
              4398315853749426
            </code>
            . Mentioning it here is transparency, not a leak.
          </li>
          <li>
            <strong>Consent controls are being implemented.</strong>{" "}
            Today the pixel fires on non-admin pages regardless of
            your consent. That&apos;s appropriate for our current
            operating region and scale; it won&apos;t stay that way
            if we start serving users in the EU or California, or
            when our ad spend meaningfully scales. The consent-gate
            code is ready to flip on when that day comes.
          </li>
        </ul>
        <p
          className="pp-customer-p mt-4 text-left"
          data-testid="privacy-meta-not-gated"
        >
          Our first-party visit counter (see the section above) is{" "}
          <em>never</em> gated on consent — it&apos;s designed to be
          legally defensible without one (cookieless, daily-rotating
          hash, no raw IP, no cross-day linkability). The consent
          gate, when it&apos;s live, applies to Meta Pixel and any
          future third-party pixel we add — nothing more.
        </p>

        {/* -------------------------------------------------------- */}
        {/* SECTION 3 — PostHog historical disclosure                */}
        {/* -------------------------------------------------------- */}
        <SectionHeading id="posthog-disclosure">
          PostHog historical disclosure
        </SectionHeading>
        <p
          className="pp-customer-p mt-4 text-left"
          data-testid="privacy-posthog-body"
        >
          A template-included third-party analytics snippet was
          discovered and removed on July 25; we have requested
          deletion of any data it collected.
        </p>
        <p
          className="pp-customer-p mt-3 text-left"
          data-testid="privacy-posthog-plain-lang"
        >
          The plain-language version: the project template we
          started from silently included a PostHog analytics script.
          It wasn&apos;t something we chose or configured —
          it slipped through with the starter code. As soon as we
          identified it on July 25, 2026, we removed the snippet
          from our public site, and on July 26, 2026 we submitted a
          formal deletion request to the platform provider asking
          them to purge any data that snippet may have collected
          while it was live.
        </p>
        <p
          className="pp-customer-p mt-3 text-left"
          data-testid="privacy-posthog-followup"
        >
          We&apos;ll update this page as we learn more. If you&apos;d
          like to check in on that in the meantime, reach out through
          the Contact link in our footer.
        </p>

        {/* -------------------------------------------------------- */}
        {/* Back CTA                                                 */}
        {/* -------------------------------------------------------- */}
        <div className="mt-12 flex items-center justify-start">
          <Link
            to="/"
            className="pp-btn pp-btn-ghost"
            data-testid="privacy-back-cta"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back to home
          </Link>
        </div>
      </article>
    </PublicPageShell>
  );
}
